import React from 'react';

import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { AppRoundedRadio } from '@vyce/core/src/components/inputs';
import { getColor } from '@vyce/core/src/theme/styles';

import { LocationAudience } from './FormRecipientsModeStep';
import { LocationAudienceCounts } from '../hooks/useLocationAudience';

const OPTIONS: {
  value: LocationAudience;
  title: string;
  hint: string;
  countKey: keyof LocationAudienceCounts;
}[] = [
  {
    value: 'everyone',
    title: 'Everyone associated with the location',
    hint: "Includes people who haven't clocked in for months — and anyone added to the location later.",
    countKey: 'everyone',
  },
  {
    value: 'clocked_in_now',
    title: 'Only people clocked in right now',
    hint: 'A live snapshot at the moment you hit Send.',
    countKey: 'clockedInNow',
  },
  {
    value: 'clocked_in_today',
    title: 'Anyone clocked in today',
    hint: 'Covers earlier shifts that have already clocked out.',
    countKey: 'clockedInToday',
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: 4,
    },
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
  },
  receiveCount: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 16,
  },
  option: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'border-color 150ms, background-color 150ms',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  optionSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: getColor(theme.palette.type === 'dark').selectedOptionBG,
  },
  optionText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    paddingTop: 5,
  },
  optionTitle: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
  },
  optionHint: {
    fontSize: 13,
    lineHeight: '18px',
    color: theme.palette.text.secondary,
  },
  optionCount: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    paddingTop: 5,
  },
  optionCountSelected: {
    color: theme.palette.primary.main,
  },
}));

const formatPeople = (count: number) => `${count} ${count === 1 ? 'person' : 'people'}`;

interface Props {
  audience: LocationAudience;
  setAudience: (audience: LocationAudience) => void;
  counts: LocationAudienceCounts;
  loading: boolean;
}

export const FormLocationAudience: React.FC<Props> = ({ audience, setAudience, counts, loading }) => {
  const classes = useStyles();
  const selectedCount = counts[OPTIONS.find(option => option.value === audience)?.countKey ?? 'everyone'];

  return (
    <Box>
      <Box className={classes.header}>
        <Typography className={classes.title}>Who at these locations should get it?</Typography>
        {!loading && selectedCount !== null && counts.everyone !== null && (
          <Typography className={classes.receiveCount}>
            {selectedCount} of {counts.everyone} will receive it
          </Typography>
        )}
      </Box>
      <Typography className={classes.subtitle}>
        Narrow by Time & Attendance activity so the form only reaches people actually working the site.
      </Typography>

      <Box className={classes.options} role="radiogroup" aria-label="Who at these locations should get it?">
        {OPTIONS.map(option => {
          const selected = option.value === audience;
          const count = counts[option.countKey];
          return (
            <label key={option.value} className={clsx(classes.option, selected && classes.optionSelected)}>
              <AppRoundedRadio
                checked={selected}
                value={option.value}
                name="form-location-audience"
                onChange={() => setAudience(option.value)}
              />
              <Box className={classes.optionText}>
                <Typography className={classes.optionTitle}>{option.title}</Typography>
                <Typography className={classes.optionHint}>{option.hint}</Typography>
              </Box>
              {loading ? (
                <Skeleton variant="text" width={64} height={24} />
              ) : (
                count !== null && (
                  <Typography className={clsx(classes.optionCount, selected && classes.optionCountSelected)}>
                    {formatPeople(count)}
                  </Typography>
                )
              )}
            </label>
          );
        })}
      </Box>
    </Box>
  );
};
